import { useEffect, useMemo } from "react";

import { PlanRateMatrixDailyRateTable } from "./daily-rate-table";
import {
	buildPlanDisplayFilterItems,
	buildRoomDisplayFilterItems,
	type DisplayFilterItems,
} from "../../model/build-display-filter-items";
import { useFetchPlanRateMatrix } from "../../model/repository/query/use-fetch-plan-rate-matrix";

type Props = {
	hotelID: string;
	yearMonth: string;
	dates: string[];
	selectedPlanIDs: string[];
	selectedRoomIDs: string[];
	selectedGuestAdultNum: number;
	onFilterSourcesChange: (sources: {
		planFilterItems: DisplayFilterItems;
		roomFilterItems: DisplayFilterItems;
		maxAdultNum: number;
	}) => void;
};

/**
 * GraphQL 取得と料金テーブル描画。
 * Suspense 境界内に置き、取得中は操作UIを消さない。
 */
export const PlanRateMatrixTableSection = ({
	hotelID,
	yearMonth,
	dates,
	selectedPlanIDs,
	selectedRoomIDs,
	selectedGuestAdultNum,
	onFilterSourcesChange,
}: Props) => {
	const { rows, rooms, maxAdultNum } = useFetchPlanRateMatrix({ hotelID, yearMonth, adultNum: selectedGuestAdultNum });

	const planFilterItems = useMemo(() => buildPlanDisplayFilterItems(rows), [rows]);
	const roomFilterItems = useMemo(() => buildRoomDisplayFilterItems(rooms), [rooms]);

	useEffect(() => {
		onFilterSourcesChange({
			planFilterItems,
			roomFilterItems,
			maxAdultNum,
		});
	}, [maxAdultNum, onFilterSourcesChange, planFilterItems, roomFilterItems]);

	const filteredRows = useMemo(() => {
		return rows.filter((row) => {
			if (!selectedPlanIDs.includes(row.planID)) return false;
			if (!selectedRoomIDs.includes(row.roomID)) return false;
			return true;
		});
	}, [rows, selectedPlanIDs, selectedRoomIDs]);

	return (
		<PlanRateMatrixDailyRateTable
			hotelID={hotelID}
			yearMonth={yearMonth}
			dates={dates}
			rows={filteredRows}
			adultNum={selectedGuestAdultNum}
		/>
	);
};

PlanRateMatrixTableSection.displayName = "PlanRateMatrixTableSection";
